import { useEffect, useId, useState } from 'react';
import type { Ricerca } from '../api/anagrafiche.js';
import { ErroreApi } from '../api/cliente.js';
import {
  type ColonnaEsportazione,
  type SceltaEsportazione,
  esportaOrdini,
  useOpzioniEsportazione,
  useSalvaEsportazione,
} from '../api/ordini.js';
import { Modale } from '../componenti/Modale.js';
import { numero } from '../formati.js';
import { t } from '../i18n/lingua.js';

/**
 * Esportare gli ordini: tre domande prima di scaricare.
 *
 * Chiesto da Rita il 26 ottobre 2026: il v2 esportava anche le righe degli
 * articoli, una per articolo con i dati dell'ordine ripetuti, e lei li usava.
 * Qui si sceglie la lingua dell'intestazione, le colonne, e se gli articoli
 * ci vanno. Le risposte restano sull'utente, sul servizio e non nel browser:
 * la volta dopo la finestra si apre com'era, anche da un altro computer.
 */
export function ModaleEsportaOrdini({
  ricerca,
  formato,
  quanti,
  onChiudi,
}: {
  ricerca: Ricerca;
  formato: 'xlsx' | 'csv';
  /** Gli ordini dell'elenco con i filtri di adesso: sono quelli che escono. */
  quanti?: number;
  onChiudi: () => void;
}) {
  const opzioni = useOpzioniEsportazione();
  const salva = useSalvaEsportazione();
  const [scelta, setScelta] = useState<SceltaEsportazione | null>(null);

  // la scelta salvata arriva una volta, e da lì in poi la si cambia qui
  useEffect(() => {
    if (opzioni.data && !scelta) setScelta(opzioni.data.choice);
  }, [opzioni.data, scelta]);

  const colonne = opzioni.data?.columns ?? [];
  const dellOrdine = colonne.filter((c) => c.group === 'order');
  const dellArticolo = colonne.filter((c) => c.group === 'line');
  const scelte = new Set(scelta?.columns ?? []);
  const quanteOrdine = dellOrdine.filter((c) => scelte.has(c.key)).length;
  const quanteArticolo = dellArticolo.filter((c) => scelte.has(c.key)).length;

  const manca = !scelta
    ? null
    : quanteOrdine === 0
      ? t('Scegli almeno una colonna dell’ordine.')
      : scelta.lines && quanteArticolo === 0
        ? t('Scegli almeno una colonna degli articoli, oppure togli «Esporta anche gli articoli».')
        : null;

  const cambia = (modifica: Partial<SceltaEsportazione>) => setScelta((s) => (s ? { ...s, ...modifica } : s));
  const spunta = (chiave: string, si: boolean) =>
    cambia({ columns: si ? [...scelte, chiave] : [...scelte].filter((c) => c !== chiave) });
  const tutte = (gruppo: ColonnaEsportazione[], si: boolean) => {
    const chiavi = new Set(gruppo.map((c) => c.key));
    cambia({
      columns: si
        ? [...new Set([...scelte, ...chiavi])]
        : [...scelte].filter((c) => !chiavi.has(c)),
    });
  };

  const errore =
    salva.error instanceof ErroreApi
      ? salva.error.message
      : opzioni.error instanceof ErroreApi
        ? opzioni.error.message
        : null;

  const esporta = () =>
    scelta &&
    salva.mutate(scelta, {
      onSuccess: () => {
        esportaOrdini(ricerca, formato);
        onChiudi();
      },
    });

  return (
    <Modale
      titolo={formato === 'xlsx' ? t('Esporta in Excel') : t('Esporta in CSV')}
      occhiello={
        quanti === undefined
          ? undefined
          : quanti === 1
            ? t('Un ordine, con i filtri di adesso')
            : t('{n} ordini, con i filtri di adesso', { n: numero(quanti) })
      }
      nota={t('Le scelte restano tue: la prossima volta la finestra si apre così.')}
      larga
      inCorso={salva.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={salva.isPending}>
            {t('Annulla')}
          </button>
          <button className="btn btn-primary" disabled={!scelta || Boolean(manca) || salva.isPending} onClick={esporta}>
            {salva.isPending ? t('Un momento…') : t('Esporta')}
          </button>
          {manca && <span className="esporta-manca">{manca}</span>}
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      {!scelta ? (
        !errore && <p className="dim">{t('Un momento…')}</p>
      ) : (
        <div className="esporta">
          <fieldset className="esporta-sezione">
            <legend className="esporta-titolo">{t('Intestazione delle colonne')}</legend>
            <div className="esporta-lingue" role="radiogroup">
              <label className="check">
                <input
                  type="radio"
                  name="esporta-lingua"
                  checked={scelta.language === 'it'}
                  onChange={() => cambia({ language: 'it' })}
                />
                <span>{t('in italiano')}</span>
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="esporta-lingua"
                  checked={scelta.language === 'en'}
                  onChange={() => cambia({ language: 'en' })}
                />
                <span>{t('in inglese')}</span>
              </label>
            </div>
            <p className="esporta-spiega">
              {t('Nella stessa lingua anche gli stati, il pagamento e le date; nomi, descrizioni e note restano come sono.')}
            </p>
          </fieldset>

          <fieldset className="esporta-sezione">
            <legend className="esporta-titolo">{t('Articoli')}</legend>
            <label className="check">
              <input type="checkbox" checked={scelta.lines} onChange={(e) => cambia({ lines: e.target.checked })} />
              <span>{t('Esporta anche gli articoli')}</span>
            </label>
            <p className="esporta-spiega">
              {scelta.lines
                ? t('Una riga per ogni articolo, con i dati dell’ordine ripetuti su ognuna. Per le somme usa «Totale riga»: il totale dell’ordine si ripete su ogni riga.')
                : t('Una riga per ogni ordine.')}
            </p>
          </fieldset>

          <GruppoColonne
            titolo={t('Colonne dell’ordine')}
            colonne={dellOrdine}
            scelte={scelte}
            onSpunta={spunta}
            onTutte={(si) => tutte(dellOrdine, si)}
          />

          {scelta.lines && (
            <GruppoColonne
              titolo={t('Colonne dell’articolo')}
              colonne={dellArticolo}
              scelte={scelte}
              onSpunta={spunta}
              onTutte={(si) => tutte(dellArticolo, si)}
            />
          )}
        </div>
      )}
    </Modale>
  );
}

function GruppoColonne({
  titolo,
  colonne,
  scelte,
  onSpunta,
  onTutte,
}: {
  titolo: string;
  colonne: ColonnaEsportazione[];
  scelte: Set<string>;
  onSpunta: (chiave: string, si: boolean) => void;
  onTutte: (si: boolean) => void;
}) {
  const quante = colonne.filter((c) => scelte.has(c.key)).length;
  const id = useId();
  return (
    <div className="esporta-sezione" role="group" aria-labelledby={id}>
      <div className="esporta-testa">
        <h3 id={id} className="esporta-titolo">
          {titolo}
          <span className="esporta-quante">{t('{n} di {m}', { n: quante, m: colonne.length })}</span>
        </h3>
        <div className="esporta-tutte">
          <button type="button" className="btn btn-ghost" onClick={() => onTutte(true)} disabled={quante === colonne.length}>
            {t('Tutte')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => onTutte(false)} disabled={quante === 0}>
            {t('Nessuna')}
          </button>
        </div>
      </div>
      <div className="esporta-colonne">
        {colonne.map((c) => (
          <label key={c.key} className="check">
            <input type="checkbox" checked={scelte.has(c.key)} onChange={(e) => onSpunta(c.key, e.target.checked)} />
            <span>{c.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
